import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Details, EDetailsType, EIconType } from '../../models/summary.model';
import { SummaryPipe } from '../../pipes/summary.pipe';
import { KeysPipe } from '../../pipes/keys.pipe';
import { ESummaryTitle } from '../../enums/summary-title.enum';

/**
 * Translation key prefix for Gov2 keys to avoid collisions
 */
const GOV2_PREFIX = 'GOV2.';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'irembogov2-summary-details',
  standalone: true,
  imports: [CommonModule, TranslateModule, SummaryPipe, KeysPipe],
  templateUrl: './summary-details.component.html',
  styleUrls: ['./summary-details.component.scss'],
})
export class Gov2SummaryDetailsComponent {
  @Output() attachmentClicked = new EventEmitter<any>();

  @Input() hasTitle = true;
  @Input() title = 'Summary Details';
  @Input() summary: Details[] = [];
  @Input() documents: any[] = [];

  showAllIndex: number | undefined;

  // Expose enums to template
  readonly EDetailsType = EDetailsType;
  readonly EIconType = EIconType;
  readonly ESummaryTitle = ESummaryTitle;

  // Labels that should be displayed as bold sub-headers
  private readonly boldLabels = [
    'reason_for_request',
    'certificate_language',
    'requested_name',
    'headquarters',
    'branch',
    'collection_office',
    'reason_for_id_correction',
    'husband',
    'wife',
    'witness_one',
    'witness_two',
    'reason_for_claim',
    'claim_explanation',
    'language_for_certificate',
  ];

  constructor(private translate: TranslateService) {}

  /**
   * Format document name for display.
   * Gov 2.0 documentName values are already human-readable strings
   * (e.g., "Environmental Impact audit certificate and report").
   * Falls back to documentCode formatted as title case.
   */
  formatDocumentName(document: any): string {
    if (document?.documentName) {
      // Try translation first, fall back to raw documentName
      const key = `${GOV2_PREFIX}${document.documentName}`;
      const translated = this.translate.instant(key);
      return translated !== key ? translated : document.documentName;
    }
    if (document?.documentCode) {
      const key = `${GOV2_PREFIX}${document.documentCode}`;
      const translated = this.translate.instant(key);
      return translated !== key
        ? translated
        : this.snakeToTitleCase(document.documentCode);
    }
    return 'Document';
  }

  previewAttachment(attachment: any): void {
    this.attachmentClicked.emit(attachment);
  }

  toggleSelectedIndex(i: number): void {
    this.showAllIndex = i;
  }

  checkRepetitiveComponent(details: any): boolean {
    if (details && details.displayFields) {
      return Object.keys(details.displayFields)?.length > 0;
    }
    return false;
  }

  /**
   * Parse repetitive component items from a section with displayFields.
   * The value contains keys whose values are JSON-encoded arrays of objects.
   * displayFields maps each key to the columns that should be shown.
   *
   * Example input:
   *   value: { list_imported_items: '[{"LIST_IMPORTED_ITEMS":"qefad","IMPORTED_QUANTITY":4}]' }
   *   displayFields: { list_imported_items: ["LIST_IMPORTED_ITEMS", "IMPORTED_QUANTITY"] }
   *
   * Returns: [{ label: "list_imported_items", columns: ["LIST_IMPORTED_ITEMS", "IMPORTED_QUANTITY"], rows: [{...}] }]
   */
  parseRepetitiveGroups(
    details: any
  ): { label: string; columns: string[]; rows: any[] }[] {
    if (!details?.displayFields || !details?.value) return [];

    const groups: { label: string; columns: string[]; rows: any[] }[] = [];

    for (const key of Object.keys(details.displayFields)) {
      const columns: string[] = details.displayFields[key] || [];
      const rawValue = details.value[key];
      let rows: any[] = [];

      if (typeof rawValue === 'string') {
        try {
          const parsed = JSON.parse(rawValue);
          rows = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          rows = [];
        }
      } else if (Array.isArray(rawValue)) {
        rows = rawValue;
      }

      groups.push({ label: key, columns, rows });
    }

    return groups;
  }

  shouldDisplaySectionTitle(details: any): boolean {
    return (
      details.icon === EIconType.OFFICE ||
      details.title === ESummaryTitle.REPRESENTATIVE_AND_WITNESSES_DETAILS ||
      details.title === ESummaryTitle.WITNESS_DETAILS_COMBINED ||
      details.title === ESummaryTitle.ADOPTIVE_PARENT_DETAILS ||
      details.title === ESummaryTitle.BIOLOGICAL_PARENT_DETAILS
    );
  }

  isArray(upload: any): boolean {
    return Array.isArray(upload);
  }

  getIconClass(): string {
    return 'icon-blue';
  }

  /**
   * Get the icon ID for the SVG sprite reference
   * e.g., EIconType.USER -> 'user'
   */
  getIconId(icon: EIconType | undefined): string {
    if (!icon) return 'other';
    return icon.toLowerCase();
  }

  /**
   * Check if a label should be displayed as a bold sub-header
   */
  isBoldLabel(key: string): boolean {
    return this.boldLabels.includes(key.toLowerCase());
  }

  /**
   * Format section title using ngx-translate with GOV2. prefix
   * e.g., REQUESTER_DETAILS -> GOV2.REQUESTER_DETAILS -> "Requester Details"
   */
  formatTitle(title: string): string {
    if (!title) return '';

    const key = `${GOV2_PREFIX}${title}`;
    const translated = this.translate.instant(key);

    // If translation found (different from key), return it
    if (translated !== key) {
      return translated;
    }

    // Fallback: convert SNAKE_CASE to Title Case
    return this.snakeToTitleCase(title);
  }

  /**
   * Format field label using ngx-translate with GOV2. prefix
   * e.g., national_id -> GOV2.national_id -> "National ID"
   */
  formatLabel(key: string): string {
    if (!key) return '';

    const translationKey = `${GOV2_PREFIX}${key}`;
    const translated = this.translate.instant(translationKey);

    // If translation found, return it
    if (translated !== translationKey) {
      return translated;
    }

    // Fallback: convert snake_case to Title Case
    return this.snakeToTitleCase(key);
  }

  /**
   * Format/translate values using ngx-translate with GOV2. prefix
   * e.g., FEMALE -> GOV2.FEMALE -> "Female"
   */
  formatValue(key: string, value: any): string {
    if (value === null || value === undefined) return '';

    const strValue = String(value);

    // Try translation with GOV2 prefix
    const translationKey = `${GOV2_PREFIX}${strValue}`;
    const translated = this.translate.instant(translationKey);

    if (translated !== translationKey) {
      return translated;
    }

    // For enum-like values (ALL_CAPS), convert to Title Case
    if (strValue === strValue.toUpperCase() && strValue.length > 1) {
      return this.snakeToTitleCase(strValue);
    }

    return strValue;
  }

  /**
   * Convert snake_case, SNAKE_CASE, camelCase, or PascalCase to Title Case
   */
  private snakeToTitleCase(str: string): string {
    if (!str) return '';

    // Insert space before uppercase letters (handles camelCase/PascalCase)
    const spaced = str.replace(/([a-z])([A-Z])/g, '$1 $2');

    return spaced
      .toLowerCase()
      .split(/[_\s]+/)
      .filter(word => word.length > 0)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  isBold(key: string, type: boolean): boolean {
    const allowedTitles = this.boldLabels;
    return type
      ? allowedTitles.includes(key.toLowerCase())
      : !allowedTitles.includes(key.toLowerCase());
  }

  translateValues(key: string, value: any): string {
    return this.formatValue(key, value?.[key]);
  }

  formatValidDate(date: any, format = 'MM/yyyy', locale = 'en'): string {
    if (date && !isNaN(new Date(date).getTime())) {
      return formatDate(new Date(date), format, locale);
    }
    return date;
  }
}
